import {
  Box,
  Typography,
  Button,
  Grid,
  TextField,
  FormControl,
  Select,
  MenuItem,
  IconButton,
  FormLabel,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  CloudUpload as UploadIcon,
  Download as DownloadIcon,
  PictureAsPdf as PictureAsPdfIcon,
  InsertDriveFile as InsertDriveFileIcon,
  Description as DescriptionIcon,
  GridOn as GridOnIcon,
  Article as ArticleIcon,
} from "@mui/icons-material";
import AutocompleteWithPagination from "../autocomplete-with-pagination";
import buildQuery from "../../utils/query-builder";
import { productService } from "../../utils/services/productService";
import { useEffect, useState } from "react";
import config from "../../config";
import { useDarkMode } from "../context/darkModeContext";
import { inputFieldSx } from "../../constants/fieldStyles";

const labelStyle = {
  mb: 1,
  fontSize: "0.875rem",
  fontFamily: "Poppins",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

const ProductRow = ({
  product,
  index,
  onUpdate,
  onRemove,
  errors,
  setErrors,
  allErrors,
  handleAttachmentChange,
  clientId,
  m2oData,
}) => {
  const { colors } = useDarkMode();

  const handleChange = (field, value) => {
    // Clear field-specific error when user starts typing
    if (
      setErrors &&
      allErrors &&
      allErrors.sku_list &&
      allErrors.sku_list[index]
    ) {
      const newErrors = { ...allErrors };
      if (newErrors.sku_list[index][field]) {
        delete newErrors.sku_list[index][field];
        setErrors(newErrors);
      }
    }

    onUpdate(index, { ...product, [field]: value });
  };
  console.log(product.attachment);

  // Helper to check if file is image
  const isImage = (fileName) => {
    if (!fileName) return false;
    return /\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(fileName);
  };

  // Build preview URL if key is present
  const previewUrl =
    product.attachment && product.attachment.key
      ? `${config.baseURL}/fetch-file/${product.attachment.key}`
      : null;

  return (
    <Box
      sx={{
        p: 2,
        bgcolor: colors.bgPrimary,
        borderRadius: 1,
        fontFamily: "Poppins",
        textAlign: "left",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography variant="subtitle2" sx={{ fontFamily: "Poppins" }}>
          Product {index + 1}
        </Typography>
        {m2oData?.sku_list?.length > 1 && (
          <IconButton
            onClick={() => onRemove(index)}
            color="error"
            size="small"
          >
            <DeleteIcon />
          </IconButton>
        )}
      </Box>
      <Grid container spacing={2}>
        <Grid item xs={12} md={10}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={12}>
              <Box sx={{ display: "flex", gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <FormLabel sx={labelStyle} required>
                    Select Product
                  </FormLabel>
                  <AutocompleteWithPagination
                    disabled={!clientId}
                    selectedClient={clientId}
                    hideLabel
                    backgroundColor="var(--bg-paper)"
                    selectedItem={product.product}
                    customLabel="Product"
                    onDataChange={(selectedProduct) => {
                      if (!selectedProduct) {
                        handleChange("product", null);
                        handleChange("sku", null);
                      } else {
                        const prevProductId = product?.product?._id;
                        const newProductId = selectedProduct?._id;

                        if (prevProductId !== newProductId) {
                          handleChange("sku", null);
                        }
                        handleChange("product", selectedProduct);
                      }
                    }}
                    width="100%"
                    optionKey="name"
                    apiEndPoint={(limit, skip, search, client) => {
                      const query = buildQuery({ limit, skip });
                      return productService.fetchOmsProducts(
                        client,
                        search,
                        query,
                      );
                    }}
                    errorMessage={errors.product}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <FormLabel sx={labelStyle} required>
                    SKU
                  </FormLabel>
                  <AutocompleteWithPagination
                    key={product?.product?._id || "no-product"}
                    hideLabel
                    backgroundColor="var(--bg-paper)"
                    customLabel="SKU"
                    selectedItem={product.sku ? product.sku : null}
                    onDataChange={(selectedSku) =>
                      handleChange("sku", selectedSku)
                    }
                    width="100%"
                    optionKey="sku"
                    data={
                      Array.isArray(product?.product?.variants)
                        ? product?.product.variants
                        : []
                    }
                    errorMessage={errors.sku}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <FormLabel sx={labelStyle} required>
                    Quantity
                  </FormLabel>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    value={product.quantity_requested || ""}
                    onChange={(e) =>
                      handleChange("quantity_requested", e.target.value)
                    }
                    error={!!errors.quantity_requested}
                    helperText={errors.quantity_requested}
                    inputProps={{ min: 1, step: 1 }}
                    sx={inputFieldSx}
                  />
                </Box>
              </Box>
            </Grid>
            <Grid style={{ paddingTop: "5px" }} item xs={12} md={12}>
              <FormLabel sx={labelStyle}>Instructions</FormLabel>
              <TextField
                size="small"
                multiline
                fullWidth
                value={product.instructions || ""}
                onChange={(e) => handleChange("instructions", e.target.value)}
                sx={inputFieldSx}
              />
            </Grid>
          </Grid>
        </Grid>
        {/* Right group: Attachment and Preview */}
        <Grid
          item
          xs={12}
          md={2}
          sx={{ display: "flex", flexDirection: "column", height: "100%" }}
        >
          <FormLabel sx={labelStyle}>Attach Additional Details</FormLabel>
          <Box
            sx={{
              flex: 1,
              minHeight: 125,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              gap: 1,
              height: 0,
              // This makes the box always fit the parent grid cell
              position: "relative",
              overflow: "hidden",
            }}
          >
            {/* Attachment Container */}
            {!previewUrl && (
              <Box
                sx={{
                  width: "100%",
                  height: "100%",
                  maxWidth: "100%",
                  maxHeight: "100%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  border: `1px dashed ${colors.border}`,
                  borderRadius: 2,
                  bgcolor: colors.bgPaper,
                  p: 2,
                  boxSizing: "border-box",
                }}
              >
                {/* If no file, show upload icon and button */}
                <UploadIcon sx={{ fontSize: 40, color: "#90caf9", mb: 1 }} />
                <Button
                  variant="outlined"
                  component="label"
                  size="small"
                  sx={{
                    fontFamily: "Poppins",
                    fontSize: 12,
                    minWidth: "70%",
                    mt: 1,
                  }}
                >
                  Choose File
                  <input
                    type="file"
                    accept="*/*"
                    hidden
                    onChange={(e) => handleAttachmentChange(e, index)}
                  />
                </Button>
              </Box>
            )}
            {/* If file is present and is an image */}
            {previewUrl && isImage(product.attachment.fileName) && (
              <Box
                sx={{
                  position: "relative",
                  width: "100%",
                  height: "125px",
                  maxWidth: "100%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  border: "1px dashed #bdbdbdbd", // match upload border
                  borderRadius: 2,
                  overflow: "hidden",
                  bgcolor: colors.bgPaper,
                  boxSizing: "border-box",
                  "&:hover .attachment-actions": { opacity: 1 },
                }}
              >
                <img
                  src={previewUrl}
                  alt={product.attachment.fileName}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    borderRadius: 2,
                    display: "block",
                  }}
                />
                <Box
                  className="attachment-actions"
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    bgcolor: "rgba(0,0,0,0.35)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 2,
                    opacity: 0,
                    transition: "opacity 0.2s",
                    borderRadius: 2,
                    zIndex: 2,
                  }}
                >
                  <IconButton
                    color="error"
                    size="small"
                    sx={{ bgcolor: "white", "&:hover": { bgcolor: "#ffeaea" } }}
                    onClick={() => {
                      handleChange("attachment", { key: "", fileName: "" });
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                  <IconButton
                    color="primary"
                    size="small"
                    sx={{ bgcolor: "white", "&:hover": { bgcolor: "#e3f2fd" } }}
                    component="a"
                    href={previewUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    download={product.attachment.fileName}
                  >
                    <DownloadIcon />
                  </IconButton>
                </Box>
              </Box>
            )}
            {/* If file is present and is NOT an image */}
            {previewUrl && !isImage(product.attachment.fileName) && (
              <Box
                sx={{
                  position: "relative",
                  width: "100%",
                  height: "125px",
                  maxWidth: "100%",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  border: `1px dashed ${colors.border}`, // match upload border
                  borderRadius: 2,
                  overflow: "hidden",
                  bgcolor: colors.bgSecondary,
                  p: 2,
                  boxSizing: "border-box",
                  "&:hover .attachment-actions": { opacity: 1 },
                  cursor: "pointer",
                }}
              >
                {/* File type icon - larger */}
                <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "100%",
                  }}
                >
                  {(() => {
                    const fileName =
                      product.attachment.fileName?.toLowerCase() || "";
                    if (fileName.endsWith(".pdf"))
                      return (
                        <PictureAsPdfIcon color="error" sx={{ fontSize: 64 }} />
                      );
                    if (fileName.endsWith(".doc") || fileName.endsWith(".docx"))
                      return (
                        <DescriptionIcon
                          sx={{ color: "#1976d2", fontSize: 64 }}
                        />
                      );
                    if (fileName.endsWith(".xls") || fileName.endsWith(".xlsx"))
                      return (
                        <Box
                          sx={{ position: "relative", display: "inline-flex" }}
                        >
                          <InsertDriveFileIcon
                            sx={{ color: "#388e3c", fontSize: 64 }}
                          />
                          <Typography
                            sx={{
                              position: "absolute",
                              right: 15,
                              bottom: 6,
                              fontSize: 13,
                              fontWeight: 700,
                              color: "white",
                              bgcolor: "#388e3c",
                              px: "3px",
                              borderRadius: "2px",
                              letterSpacing: 0.5,
                              pointerEvents: "none",
                            }}
                          >
                            XLS
                          </Typography>
                        </Box>
                      );
                    if (fileName.endsWith(".txt"))
                      return (
                        <ArticleIcon sx={{ color: "#616161", fontSize: 64 }} />
                      );
                    return (
                      <InsertDriveFileIcon
                        sx={{ color: "#757575", fontSize: 64 }}
                      />
                    );
                  })()}
                </Box>
                {/* File name - smaller */}
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: "Poppins",
                    fontSize: 12,
                    color: colors.textPrimary,
                    maxWidth: "100%",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    mb: 1,
                  }}
                >
                  {product.attachment.fileName}
                </Typography>
                <Box
                  className="attachment-actions"
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    bgcolor: "rgba(0,0,0,0.35)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 2,
                    opacity: 0,
                    transition: "opacity 0.2s",
                    borderRadius: 2,
                    zIndex: 2,
                  }}
                >
                  <IconButton
                    color="error"
                    size="small"
                    sx={{ bgcolor: "white", "&:hover": { bgcolor: "#ffeaea" } }}
                    onClick={() => {
                      onUpdate(index, {
                        ...product,
                        attachment: { key: "", fileName: "" },
                      });
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                  <IconButton
                    color="primary"
                    size="small"
                    sx={{ bgcolor: "white", "&:hover": { bgcolor: "#e3f2fd" } }}
                    component="a"
                    href={previewUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    download={product.attachment.fileName}
                  >
                    <DownloadIcon />
                  </IconButton>
                </Box>
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ProductRow;
